import { Injectable } from '@angular/core';
import { API as AWS_API } from 'aws-amplify';
import * as moment from 'moment';
import { from, Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { CONST_AWS_API } from 'src/app/core/constants';
import { environment } from 'src/environments/environment';
import { TopMenuEvent } from 'src/app/pages/components/indigenous-cloud/ic-top-menu/top-menu-events.model';

/**
 * Live and upcoming events for the network top menu.
 * Same EVENTS call and body as the Events page; one cached request per network and group.
 */
@Injectable({
  providedIn: 'root'
})
export class TopMenuEventsService {
  private cache = new Map<string, Observable<TopMenuEvent[]>>();

  getEvents(organizationId: number, networkGroupId?: number): Observable<TopMenuEvent[]> {
    if (!organizationId) {
      return of([]);
    }
    const key = `${organizationId}:${networkGroupId || 0}`;
    if (!this.cache.has(key)) {
      this.cache.set(key, this.request(organizationId, networkGroupId).pipe(shareReplay(1)));
    }
    return this.cache.get(key);
  }

  private request(organizationId: number, networkGroupId?: number): Observable<TopMenuEvent[]> {
    const init = {
      headers: { 'x-api-key': environment.AWS_Cognito.X_Api_Key },
      response: true,
      body: {
        organization_id: organizationId,
        start_date: moment().startOf('day').toDate(),
        end_date: moment().startOf('day').add(3, 'months').endOf('day').toDate(),
        ...(networkGroupId && { network_group_id: networkGroupId }),
      },
    };
    return from(AWS_API.post(environment.AWS_Cognito.apiName, CONST_AWS_API.EVENTS, init)).pipe(
      map((response: any) => response?.data?.statusCode === '200' ? mapTopMenuEvents(response.data.Data) : []),
      catchError(() => of([])),
    );
  }
}

/** Published upcoming events, live ones first, then by start time. */
export function mapTopMenuEvents(data: any): TopMenuEvent[] {
  const liveIds = new Set((data?.live_events || []).map((live: any) => live?.stream_info?.parent_streams_id));
  return (data?.upcoming_events || [])
    .filter((item: any) => item?.stream_info?.publish == 1 && item.stream_info.slug)
    .map((item: any) => {
      const info = item.stream_info;
      return {
        id: info.id,
        title: info.stream_video_title || '',
        slug: info.slug,
        networkGroupId: Number(info.network_group_id) || 0,
        groupSlug: info.group_slug || '',
        channelName: info.channel_name || '',
        start: moment.utc(info.publish_date_time).toDate(),
        isLive: liveIds.has(info.id),
        line: item.Category_info?.category_name || '',
      };
    })
    .sort((a: TopMenuEvent, b: TopMenuEvent) =>
      Number(b.isLive) - Number(a.isLive) || a.start.getTime() - b.start.getTime());
}
